import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { REVIEW_TITLE_MAX_LENGTH, REVIEW_TEXT_MAX_LENGTH } from '../utils/validation';
import { flagReview, getReviewVote, voteOnReview } from '../services/firestoreService';

const moderationBadge = (status) => {
  if (status === 'approved') {
    return null; // Don't show badge for approved reviews
  }
  if (status === 'flagged') {
    return <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">Flagged</span>;
  }
  return <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">Pending</span>;
};

const VIDEO_EXTENSION_PATTERN = /\.(mp4|mov|m4v|webm)(\?|$)/i;
const THUMBNAIL_TAP_MAX_MOVEMENT_PX = 10;

function isVideoUrl(url = '') {
  return VIDEO_EXTENSION_PATTERN.test(url) || url.includes('/video/upload/');
}

function getCloudinaryMp4Url(url = '') {
  if (!url.includes('/video/upload/')) return url;
  if (url.includes('/video/upload/f_mp4,')) return url;
  return url.replace('/video/upload/', '/video/upload/f_mp4,vc_auto,q_auto/');
}

const ThumbnailStrip = React.memo(function ThumbnailStrip({ mediaItems, onThumbnailClick }) {
  const pressStartRef = useRef({ x: 0, y: 0 });
  const movedRef = useRef(false);

  if (!mediaItems.length) return null;

  const handlePointerDown = (event) => {
    pressStartRef.current = { x: event.clientX, y: event.clientY };
    movedRef.current = false;
  };

  const handlePointerMove = (event) => {
    const dx = Math.abs(event.clientX - pressStartRef.current.x);
    const dy = Math.abs(event.clientY - pressStartRef.current.y);
    if (dx > THUMBNAIL_TAP_MAX_MOVEMENT_PX || dy > THUMBNAIL_TAP_MAX_MOVEMENT_PX) {
      movedRef.current = true;
    }
  };

  const handlePointerUp = (event, index) => {
    event.stopPropagation();
    if (movedRef.current) return;
    onThumbnailClick(index);
  };

  const handleKeyDown = (event, index) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onThumbnailClick(index);
    }
  };

  return (
    <div className="mt-3">
      <p className="mb-1.5 text-xs font-medium text-slate-400">Media</p>
      <div className="flex flex-wrap gap-2">
        {mediaItems.slice(0, 4).map((item, index) => {
          const isOverflow = index === 3 && mediaItems.length > 4;
          const extraCount = mediaItems.length - 4;
          return (
            <button
              key={item.url}
              type="button"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={(event) => handlePointerUp(event, index)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              className="group relative h-[72px] w-[72px] flex-shrink-0 cursor-pointer overflow-hidden rounded-[8px] shadow-sm"
              style={{ touchAction: 'manipulation' }}
              aria-label={`Open media ${index + 1}`}
            >
              {item.isVideo ? (
                <video
                  src={item.playbackUrl}
                  className="pointer-events-none h-full w-full object-cover"
                  muted
                  preload="metadata"
                />
              ) : (
                <img src={item.url} alt={`Media ${index + 1}`} className="pointer-events-none h-full w-full object-cover" loading="lazy" />
              )}
              <div className="pointer-events-none absolute inset-0 bg-black/0 group-hover:bg-black/20" />
              {item.isVideo && !isOverflow && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="rounded-full bg-black/50 p-1.5">
                    <svg viewBox="0 0 20 20" fill="white" className="h-4 w-4">
                      <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                    </svg>
                  </div>
                </div>
              )}
              {isOverflow && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                  <span className="text-sm font-bold text-white">+{extraCount + 1}</span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
});

const LightboxOverlay = React.memo(function LightboxOverlay({
  mediaItems,
  lightbox,
  onBackdropClick,
  onClose,
  onPrev,
  onNext,
  onGoToSlide,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  pauseAllVideos,
  lightboxVideoRefs,
}) {
  const currentItem = mediaItems[lightbox.currentIndex] || null;
  const [loadedByUrl, setLoadedByUrl] = useState({});

  useEffect(() => {
    if (!lightbox.isOpen || !currentItem) return;
    setLoadedByUrl((prev) => (currentItem.url in prev ? prev : { ...prev, [currentItem.url]: false }));
  }, [lightbox.isOpen, currentItem]);

  useEffect(() => {
    if (!lightbox.isOpen || !mediaItems.length) return;
    const next = mediaItems[(lightbox.currentIndex + 1) % mediaItems.length];
    const prev = mediaItems[(lightbox.currentIndex - 1 + mediaItems.length) % mediaItems.length];
    [next, prev]
      .filter((item) => item && !item.isVideo)
      .forEach((item) => {
        const img = new Image();
        img.src = item.url;
      });
  }, [lightbox.isOpen, lightbox.currentIndex, mediaItems]);

  const markLoaded = useCallback((url) => {
    setLoadedByUrl((prev) => (prev[url] ? prev : { ...prev, [url]: true }));
  }, []);

  const isCurrentLoaded = currentItem ? Boolean(loadedByUrl[currentItem.url]) : true;

  const overlayStyle = {
    backgroundColor: 'rgba(0,0,0,0.92)',
    opacity: lightbox.isOpen ? 1 : 0,
    pointerEvents: lightbox.isOpen ? 'all' : 'none',
    visibility: lightbox.isOpen ? 'visible' : 'hidden',
    willChange: 'opacity',
    backfaceVisibility: 'hidden',
    transform: 'translateZ(0)',
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center transition-opacity duration-200"
      style={overlayStyle}
      onClick={onBackdropClick}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      aria-hidden={!lightbox.isOpen}
    >
      <div className="absolute left-4 top-4 z-20 rounded-full bg-black/50 px-3 py-1 text-sm font-semibold text-white">
        {mediaItems.length ? `${lightbox.currentIndex + 1} / ${mediaItems.length}` : '0 / 0'}
      </div>

      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/25"
        aria-label="Close media preview"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
          <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22z" />
        </svg>
      </button>

      {mediaItems.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute left-3 top-1/2 z-20 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/25"
            aria-label="Previous media"
          >
            <svg viewBox="0 0 20 20" fill="none" className="h-6 w-6">
              <path d="M12.5 4.5L7 10l5.5 5.5" stroke="white" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-3 top-1/2 z-20 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/25"
            aria-label="Next media"
          >
            <svg viewBox="0 0 20 20" fill="none" className="h-6 w-6">
              <path d="M7.5 4.5L13 10l-5.5 5.5" stroke="white" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </>
      )}

      {lightbox.isOpen && !isCurrentLoaded && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/30 border-t-white" />
        </div>
      )}

      <div
        className="relative flex h-full w-full items-center justify-center overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex h-full w-full items-center justify-center px-16">
          {currentItem && currentItem.isVideo ? (
            <video
              key={`video-${currentItem.url}`}
              ref={(el) => { lightboxVideoRefs.current[currentItem.url] = el; }}
              controls
              className={`max-h-[85vh] max-w-[90vw] object-contain transition-opacity duration-150 ${isCurrentLoaded ? 'opacity-100' : 'opacity-0'}`}
              onLoadedData={() => markLoaded(currentItem.url)}
              onPlay={() => pauseAllVideos(currentItem.url)}
              onClick={(e) => e.stopPropagation()}
            >
              <source src={currentItem.playbackUrl} type="video/mp4" />
              <source src={currentItem.url} type="video/quicktime" />
            </video>
          ) : currentItem ? (
            <img
              src={currentItem.url}
              alt="Review media full screen"
              className={`max-h-[85vh] max-w-[90vw] object-contain transition-opacity duration-150 ${isCurrentLoaded ? 'opacity-100' : 'opacity-0'}`}
              style={{ touchAction: 'pinch-zoom' }}
              onLoad={() => markLoaded(currentItem.url)}
              onClick={(e) => e.stopPropagation()}
            />
          ) : null}
        </div>
      </div>

      {mediaItems.length > 1 && (
        <div
          className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/40 px-3 py-2"
          onClick={(e) => e.stopPropagation()}
        >
          {mediaItems.map((item, index) => (
            <button
              key={`dot-${item.url}`}
              type="button"
              onClick={() => onGoToSlide(index)}
              className={`h-2 w-2 rounded-full transition ${
                index === lightbox.currentIndex ? 'bg-white' : 'bg-white/35'
              }`}
              aria-label={`Go to media ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
});

function ReviewCard({ review, onEdit, onDelete, isEditing, editForm, setEditForm, onSaveEdit, onCancelEdit, showOwnershipBadge = false }) {
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const { user } = useContext(AuthContext);
  const isOwnReview = Boolean(user && review.user_id && user.id === review.user_id);
  const [upvotes, setUpvotes] = useState(review.upvotes || 0);
  const [downvotes, setDownvotes] = useState(review.downvotes || 0);
  const [flagCount, setFlagCount] = useState(review.flag_count || 0);
  const [userVote, setUserVote] = useState(review.user_vote || null);
  const [userFlagged, setUserFlagged] = useState(false);
  const [showFlagReason, setShowFlagReason] = useState(false);
  const isLiked = userVote === 'up';

  useEffect(() => {
    setUserVote(review.user_vote || null);
  }, [review.user_vote]);

  useEffect(() => {
    if (!review.id || !user) return undefined;
    let mounted = true;

    getReviewVote(review.id)
      .then((vote) => {
        if (mounted) {
          setUserVote(vote);
        }
      })
      .catch(() => {
        // Fail silently; existing vote state may still be valid.
      });

    return () => {
      mounted = false;
    };
  }, [review.id, user]);
  const [flagReason, setFlagReason] = useState('');
  const [flagError, setFlagError] = useState('');
  const [voteLoading, setVoteLoading] = useState(false);
  const [voteError, setVoteError] = useState('');
  const [lightbox, setLightbox] = useState({ isOpen: false, currentIndex: 0 });
  const lightboxTouchStartRef = useRef(null);
  const lightboxTouchCurrentRef = useRef(null);
  const lightboxVideoRefs = useRef({});
  const editTitleLength = (editForm?.title || '').length;
  const editTextLength = (editForm?.text || '').length;

  const mediaItems = useMemo(() => (review.media_urls || []).map((url) => ({
    url,
    isVideo: isVideoUrl(url),
    playbackUrl: getCloudinaryMp4Url(url)
  })), [review.media_urls]);

  const handleVote = useCallback(async (type) => {
    if (!user || voteLoading) return;
    setVoteLoading(true);
    setVoteError('');
    try {
      const data = await voteOnReview(review.id, type);
      setUpvotes(data.upvotes);
      setDownvotes(data.downvotes);
      setUserVote(data.user_vote ?? null);
    } catch (e) {
      console.error('[Vote error]', e);
      setVoteError(e?.message || 'Unable to vote. Please try again.');
    } finally { setVoteLoading(false); }
  }, [review.id, user, voteLoading]);

  const handleUpvote = useCallback(() => {
    handleVote('up');
  }, [handleVote]);

  const handleDownvote = useCallback(() => {
    handleVote('down');
  }, [handleVote]);

  const handleFlag = async (reason = '') => {
    if (!user || voteLoading) return;
    setVoteLoading(true);
    setFlagError('');
    try {
      const data = await flagReview(review.id, reason);
      setFlagCount(data.flag_count);
      setUserFlagged(data.flagged);
      if (data.flagged) {
        setFlagReason('');
        setShowFlagReason(false);
      }
    } catch (e) {
      const message = e?.message || 'Unable to submit flag';
      setFlagError(message);
      if (message.includes('already flagged')) {
        setUserFlagged(true);
        setShowFlagReason(false);
      }
    }
    finally { setVoteLoading(false); }
  };

  const onFlagClick = () => {
    if (!user || voteLoading) return;
    if (userFlagged) {
      setFlagError('You already flagged this review. Flag comments cannot be edited.');
      return;
    }
    setFlagError('');
    setShowFlagReason(true);
  };

  const submitFlagReason = () => {
    const trimmed = flagReason.trim();
    if (!trimmed) {
      setFlagError('Please add a reason before flagging.');
      return;
    }
    handleFlag(trimmed);
  };

  const handleDelete = (reviewId) => {
    setDeleteConfirm(reviewId);
  };

  const confirmDelete = async () => {
    if (onDelete) {
      await onDelete(deleteConfirm);
      setDeleteConfirm(null);
    }
  };

  const validateFile = (file) => {
    const allowedMimeTypes = ['image/jpeg', 'image/png', 'image/webp', 'video/mp4', 'video/quicktime'];
    const maxSize = 10 * 1024 * 1024;

    if (!allowedMimeTypes.includes(file.type)) {
      alert('Only JPG, PNG, WEBP, MP4, and MOV files are allowed.');
      return false;
    }

    if (file.size > maxSize) {
      alert('File size exceeds 10 MB per file.');
      return false;
    }
    return true;
  };

  const handleFiles = (event) => {
    const uploaded = Array.from(event.target.files).filter(validateFile).map((file) => ({
      type: 'new',
      file,
      preview: URL.createObjectURL(file)
    }));
    setEditForm({ ...editForm, media: [...(editForm.media || []), ...uploaded].slice(0, 5) });
  };

  const removeFile = (index) => {
    setEditForm({ ...editForm, media: editForm.media.filter((_, idx) => idx !== index) });
  };

  const pauseAllVideos = useCallback((exceptUrl = null) => {
    Object.entries(lightboxVideoRefs.current).forEach(([url, video]) => {
      if (video && url !== exceptUrl) video.pause();
    });
  }, []);

  const closeLightbox = useCallback(() => {
    pauseAllVideos();
    setLightbox((prev) => (prev.isOpen ? { ...prev, isOpen: false } : prev));
  }, [pauseAllVideos]);

  const handleBackdropClick = useCallback((event) => {
    // Close only when the actual backdrop is clicked, not inner lightbox content.
    if (event.target === event.currentTarget) {
      closeLightbox();
    }
  }, [closeLightbox]);

  const goToLightboxSlide = useCallback((index) => {
    if (!mediaItems.length) return;
    pauseAllVideos();
    setLightbox((prev) => ({ ...prev, currentIndex: (index + mediaItems.length) % mediaItems.length }));
  }, [mediaItems.length, pauseAllVideos]);

  const goToLightboxNext = useCallback(() => {
    if (!mediaItems.length) return;
    pauseAllVideos();
    setLightbox((prev) => ({ ...prev, currentIndex: (prev.currentIndex + 1) % mediaItems.length }));
  }, [mediaItems.length, pauseAllVideos]);

  const goToLightboxPrev = useCallback(() => {
    if (!mediaItems.length) return;
    pauseAllVideos();
    setLightbox((prev) => ({ ...prev, currentIndex: (prev.currentIndex - 1 + mediaItems.length) % mediaItems.length }));
  }, [mediaItems.length, pauseAllVideos]);

  const handleLightboxTouchEnd = useCallback(() => {
    if (!lightbox.isOpen) return;
    const start = lightboxTouchStartRef.current;
    const current = lightboxTouchCurrentRef.current;

    if (!start || !current) {
      lightboxTouchStartRef.current = null;
      lightboxTouchCurrentRef.current = null;
      return;
    }

    const deltaX = start.x - current.x;
    const deltaY = start.y - current.y;

    // Ignore vertical scrolling gestures; only treat clear horizontal drags as slide navigation.
    if (Math.abs(deltaX) > 60 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
      if (deltaX > 0) goToLightboxNext();
      else goToLightboxPrev();
    }

    lightboxTouchStartRef.current = null;
    lightboxTouchCurrentRef.current = null;
  }, [goToLightboxNext, goToLightboxPrev, lightbox.isOpen]);

  const handleLightboxTouchStart = useCallback((event) => {
    if (!lightbox.isOpen) return;
    const touch = event.touches?.[0];
    if (!touch) return;
    lightboxTouchStartRef.current = { x: touch.clientX, y: touch.clientY };
    lightboxTouchCurrentRef.current = { x: touch.clientX, y: touch.clientY };
  }, [lightbox.isOpen]);

  const handleLightboxTouchMove = useCallback((event) => {
    if (!lightbox.isOpen) return;
    const touch = event.touches?.[0];
    if (!touch) return;
    lightboxTouchCurrentRef.current = { x: touch.clientX, y: touch.clientY };
  }, [lightbox.isOpen]);

  const openLightbox = useCallback((index) => {
    setLightbox({ isOpen: true, currentIndex: index });
  }, []);

  useEffect(() => {
    if (mediaItems.length === 0) return;
    setLightbox((prev) => (prev.currentIndex >= mediaItems.length ? { ...prev, currentIndex: 0 } : prev));
  }, [mediaItems.length]);

  useEffect(() => {
    if (!lightbox.isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') closeLightbox(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [lightbox.isOpen, closeLightbox]);

  useEffect(() => {
    if (!lightbox.isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [lightbox.isOpen]);

  if (isEditing) {
    return (
      <article className="overflow-hidden rounded-[28px] border border-blue-300 bg-blue-50 p-6 shadow-sm">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Edit Review</h3>
        </div>
        <form onSubmit={onSaveEdit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
            <input
              value={editForm.title || ''}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              maxLength={REVIEW_TITLE_MAX_LENGTH}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <p className={`mt-1 text-xs ${editTitleLength >= REVIEW_TITLE_MAX_LENGTH - 10 ? 'text-rose-600' : 'text-slate-500'}`}>
              {editTitleLength}/{REVIEW_TITLE_MAX_LENGTH}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Review Text</label>
            <textarea
              value={editForm.text}
              onChange={(e) => setEditForm({ ...editForm, text: e.target.value })}
              maxLength={REVIEW_TEXT_MAX_LENGTH}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="4"
              required
            />
            <p className={`mt-1 text-xs ${editTextLength >= 490 ? 'text-rose-600' : editTextLength >= 450 ? 'text-amber-600' : 'text-slate-500'}`}>
              {editTextLength}/{REVIEW_TEXT_MAX_LENGTH}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Upload media <span className="text-slate-500">(OPTIONAL)</span></label>
            <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-slate-900">Images and videos</p>
                  <p className="text-sm text-slate-500">JPG, PNG, WEBP, MP4, MOV. Max 10MB per file. Up to 5 files.</p>
                </div>
                <label className="inline-flex cursor-pointer rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800">
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,.mov"
                    onChange={handleFiles}
                    className="hidden"
                  />
                  Choose files
                </label>
              </div>
              {editForm.media && editForm.media.length > 0 && (
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {editForm.media.map((item, index) => (
                    <div key={item.url || item.preview || index} className="relative overflow-hidden rounded-3xl border border-slate-200 bg-slate-50">
                      {(item.file?.type || '').startsWith('video/') || /\.(mp4)$/i.test(item.preview || item.url || '') ? (
                        <video controls className="h-24 w-full object-cover">
                          <source src={item.preview || item.url} type={item.file?.type || 'video/mp4'} />
                        </video>
                      ) : (
                        <img src={item.preview || item.url} alt={`Upload ${index + 1}`} className="h-24 w-full object-cover" />
                      )}
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="absolute right-2 top-2 rounded-full bg-red-500 p-1 text-white hover:bg-red-600"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Save Changes
            </button>
            <button
              type="button"
              onClick={onCancelEdit}
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </form>
      </article>
    );
  }

  return (
    <article className="overflow-hidden rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-xl font-semibold text-slate-900">{review.title || 'Incident Report'}</h3>
            {showOwnershipBadge && isOwnReview && (
              <span className="inline-flex rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700">
                My Comment
              </span>
            )}
          </div>
          <p className="mt-2 text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
            <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-sm font-semibold tracking-[0.16em] text-slate-700 dark:bg-slate-700 dark:text-slate-100">
              {review.plate}
            </span>
            <span className="ml-1.5 inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-sm font-semibold tracking-[0.16em] text-slate-700 dark:bg-slate-700 dark:text-slate-100">
              {review.state}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {moderationBadge(review.moderation_status)}
          {onEdit && onDelete && (
            <div className="flex gap-2">
              <button
                onClick={() => onEdit(review)}
                className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800 hover:bg-blue-200"
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(review.id)}
                className="rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-800 hover:bg-red-200"
              >
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      <ThumbnailStrip mediaItems={mediaItems} onThumbnailClick={openLightbox} />

      <p className="mt-4 text-sm leading-7 text-slate-700 whitespace-pre-wrap">{review.text}</p>

      <div className="mt-6 flex flex-col gap-3 border-t border-slate-200 pt-4 text-sm text-slate-500 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <span>Last Reported on : {new Date(review.created_at).toLocaleDateString()}</span>
        {/* Vote and Flag row */}
        <div className="flex items-center gap-2 sm:ml-auto">
          {!user && <span className="text-xs text-slate-400 mr-1">Sign in to vote</span>}
          {voteError && <span className="text-xs text-red-500 mr-1">{voteError}</span>}
          <button
            type="button"
            onClick={handleUpvote}
            disabled={!user || voteLoading}
            title="Upvote"
            className={`flex min-h-[36px] items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition border ${isLiked ? 'bg-[#1b5e20] text-white border-[#1b5e20]' : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'} disabled:opacity-50 disabled:cursor-default`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
              <path d="M7.493 18.79v-6.838a2.25 2.25 0 0 1 .658-1.591l5.72-5.72a.75.75 0 0 1 1.28.53v3.286a.75.75 0 0 0 .75.75h4.098a2.25 2.25 0 0 1 2.244 2.424l-.426 6.08a2.25 2.25 0 0 1-2.244 2.093h-8.069a2.25 2.25 0 0 1-2.25-2.25Zm-3.75.54a1.5 1.5 0 0 0 1.5 1.5h.75a.75.75 0 0 0 .75-.75V11.14a.75.75 0 0 0-.75-.75h-.75a1.5 1.5 0 0 0-1.5 1.5v7.44Z" />
            </svg>
            {upvotes}
          </button>
          <button
            type="button"
            onClick={handleDownvote}
            disabled={!user || voteLoading}
            title="Downvote"
            className={`flex min-h-[36px] items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition border ${userVote === 'down' ? 'bg-red-500 text-white border-red-500' : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'} disabled:opacity-50 disabled:cursor-default`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
              <path d="M16.507 5.21v6.838a2.25 2.25 0 0 1-.658 1.591l-5.72 5.72a.75.75 0 0 1-1.28-.53v-3.286a.75.75 0 0 0-.75-.75H4.001a2.25 2.25 0 0 1-2.244-2.424l.426-6.08A2.25 2.25 0 0 1 4.427 4.196h8.069a2.25 2.25 0 0 1 2.25 2.25Zm3.75-.54a1.5 1.5 0 0 0-1.5-1.5h-.75a.75.75 0 0 0-.75.75v8.94a.75.75 0 0 0 .75.75h.75a1.5 1.5 0 0 0 1.5-1.5V4.67Z" />
            </svg>
            {downvotes}
          </button>
          {!isOwnReview && (
          <button
            type="button"
            onClick={onFlagClick}
            disabled={!user || voteLoading || userFlagged}
            title={userFlagged ? 'You already flagged this review' : 'Flag this report'}
            className={`flex min-h-[36px] items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition border ${userFlagged ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'} disabled:opacity-50 disabled:cursor-default`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5">
              <path d="M3.5 2.75a.75.75 0 0 0-1.5 0v14.5a.75.75 0 0 0 1.5 0v-4.392l1.657-.348a6.449 6.449 0 0 1 4.271.572 7.948 7.948 0 0 0 5.965.524l2.078-.64A.75.75 0 0 0 18 12.25v-8.5a.75.75 0 0 0-.904-.734l-2.38.501a7.25 7.25 0 0 1-4.186-.363l-.502-.2a8.75 8.75 0 0 0-5.028-.176L3.5 3.233V2.75Z" />
            </svg>
            {flagCount > 0 && <span>{flagCount}</span>}
          </button>
          )}
        </div>
      </div>

      {showFlagReason && user && !userFlagged && (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
          <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-slate-600">
            Why is this report bad?
          </label>
          <textarea
            value={flagReason}
            onChange={(e) => setFlagReason(e.target.value)}
            placeholder="Explain why this report should be flagged..."
            rows={3}
            className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
          />
          {flagError && <p className="mt-2 text-xs text-rose-600">{flagError}</p>}
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={submitFlagReason}
              disabled={voteLoading}
              className="rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
            >
              Submit Flag
            </button>
            <button
              type="button"
              onClick={() => {
                setShowFlagReason(false);
                setFlagReason('');
                setFlagError('');
              }}
              className="rounded-full bg-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-slate-700 mb-3">Are you sure you want to delete this review? This action cannot be undone.</p>
          <div className="flex gap-2">
            <button
              onClick={confirmDelete}
              className="bg-red-600 text-white px-3 py-1 rounded-lg hover:bg-red-700 text-sm"
            >
              Delete
            </button>
            <button
              onClick={() => setDeleteConfirm(null)}
              className="bg-gray-300 text-gray-700 px-3 py-1 rounded-lg hover:bg-gray-400 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <LightboxOverlay
        mediaItems={mediaItems}
        lightbox={lightbox}
        onBackdropClick={handleBackdropClick}
        onClose={closeLightbox}
        onPrev={goToLightboxPrev}
        onNext={goToLightboxNext}
        onGoToSlide={goToLightboxSlide}
        onTouchStart={handleLightboxTouchStart}
        onTouchMove={handleLightboxTouchMove}
        onTouchEnd={handleLightboxTouchEnd}
        pauseAllVideos={pauseAllVideos}
        lightboxVideoRefs={lightboxVideoRefs}
      />
    </article>
  );
}

export default React.memo(ReviewCard);
